import {
  InteractionStepType,
  InteractionStepConfig,
  StepParamsForType,
  StepParamsForTypeWithStage,
  IndexedInteractionStepResponse,
} from '@scale/shared/types/chat';
import { IMultiTurnThreadHistory } from '../../ThreadHistory.interface';
import StepDescriptor from '../stepDescriptor/StepDescriptor';
import { schema } from './validation';
import MultiTurnContinueViewComponent from './MultiTurnContinueViewComponent';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';
import { ReviewLevel } from '@scale/shared-types';
import { validateRequiredReferenceTextsAndAttachments } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/PromptInput/utils';

export class MultiTurnContinueDescriptor extends StepDescriptor<InteractionStepType.MultiTurnContinue> {
  type = InteractionStepType.MultiTurnContinue;
  ViewComponent = MultiTurnContinueViewComponent;

  title = InteractionStepConfig[InteractionStepType.MultiTurnContinue].title;
  description = InteractionStepConfig[InteractionStepType.MultiTurnContinue].description;

  validationSchema = schema;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.MultiTurnContinue>,
    history: IMultiTurnThreadHistory,
    _reviewLevel?: ReviewLevel,
  ) {
    if (!response.context.shouldContinue || response.context.skipPromptInput) {
      return { canSubmit: true, submitTooltip: '' };
    }

    const incompletePrompt = !response.output || response.output.length === 0;

    /*
     * Empty default user messages are current valid configurations
     * TODO: Ideally, we deprecate this and prevent empty default user messages in the first place
     */
    if (!history.threadParams.useDefaultUserMessage && incompletePrompt) {
      return { canSubmit: false, submitTooltip: 'Some prompts have not been completed.' };
    }

    /*
     * Note: turnIndex must be a 0-indexed representation of the turn number
     * stepParams.turn is actually two behind the turn number, so we add one to get a 0-indexed representation
     */
    const { canSubmit, submitTooltip } = validateRequiredReferenceTextsAndAttachments(
      response,
      history.threadParams,
      stepParams.turn + 1,
    );

    if (!canSubmit) {
      return { canSubmit, submitTooltip };
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths() {
    return [
      {
        path: 'output',
        label: 'Prompt',
        hideFromAnswerKey: true,
      },
    ];
  }

  generateEmptyResponse(
    _stepParams: StepParamsForType<InteractionStepType.MultiTurnContinue>,
    history: IMultiTurnThreadHistory,
  ) {
    // Need to account for initialMessages once supporting MultiTurn templating
    const lastMessage = history.getMostRecentResponse();
    return {
      type: InteractionStepType.MultiTurnContinue,
      context: { message: lastMessage, shouldContinue: false },
      output: '',
    };
  }
}

registerStepDescriptor({
  type: InteractionStepType.MultiTurnContinue,
  descriptor: new MultiTurnContinueDescriptor(),
});
